import {cn} from '@tempmee/core'
import {SolidStarIcon} from '@tempmee/react-icons'
import {forwardRef} from 'react'
import {Typography} from '../typography'

export type RatingValueTone = 'default' | 'success'

/**
 * Star color per tone. `default` is the muted reading of the Figma "Rating
 * Display Small" node, where the whole summary sits on `text/secondary`
 * alongside the meta text it lines up with; `success` tints the star green.
 */
const STAR_CLASS: Record<RatingValueTone, string> = {
  default: 'text-text-secondary',
  success: 'text-success',
}

/**
 * Numeric text color per tone — it follows the star on `default` so the summary
 * reads as one unit, and stays primary under `success`, where only the star
 * carries the positive signal.
 */
const NUMBER_CLASS: Record<RatingValueTone, string> = {
  default: 'text-text-secondary',
  success: 'text-text-primary',
}

export interface RatingValueProps {
  /** The average/score to display (rendered with one decimal, e.g. `4.8`). */
  value: number
  /** Star tone. @default 'default' */
  tone?: RatingValueTone
  className?: string
}

/**
 * Compact "★ 4.8" rating summary — a single star plus the numeric score.
 * Use for dense surfaces (cards, list rows) where a full {@link Rating} row
 * is too large. `tone="success"` tints the star green for a positive score.
 */
export const RatingValue = forwardRef<HTMLDivElement, RatingValueProps>(
  ({value, tone = 'default', className}, ref) => (
    <div ref={ref} className={cn('inline-flex items-center gap-0.5', className)}>
      <SolidStarIcon size={16} className={STAR_CLASS[tone]} aria-hidden="true" />
      <Typography variant="body1" as="span" className={NUMBER_CLASS[tone]}>
        {value.toFixed(1)}
      </Typography>
    </div>
  ),
)

RatingValue.displayName = 'RatingValue'
